/**
 * ExternalTreatmentForm — editor for external prescriptions (wet scripts).
 *
 * Covers both API types: `external_prescription` (written script, no stock
 * impact) and `external_prescription_with_task` (written script + clinic task
 * for printing/signing).
 *
 * Products are selected from the universal catalog. If one doesn't exist yet,
 * the user can create it inline.
 */
import { useStore } from '@tanstack/react-form'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { z } from 'zod'

import type { ClinicId, PrescribingContext } from '@snootsvet/shared'

import { ClinicSearchWithDefault } from '@/frontend/components/clinic-search'
import { useAppForm } from '@/frontend/components/form'
import { FieldWrapper } from '@/frontend/components/form/fields'
import { zodErrorToFieldErrors } from '@/frontend/components/form/validation'
import { Button } from '@/frontend/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { CatalogProductForm } from '@/frontend/features/inventory/catalog-product.form'
import { CatalogProductSearch } from '@/frontend/features/inventory/catalog-product-search'
import { useListCatalogProducts } from '@/frontend/features/inventory/use-list-catalog-products'

import type { ExternalFormItem } from './treatments.types'

// ---------------------------------------------------------------------------
// Form values & schemas
// ---------------------------------------------------------------------------

/** Pre-validation form state — all fields nullable for empty-form state. */
interface ExternalFormValues {
  catalogProductId: string | null
  quantity: number | null
  repeatsAllowed: number | null
  instructions: string | null
  validUntil: Date | null
  veterinaryCascade: boolean
  createTask: boolean
  clinicId: ClinicId | null
  clinicalNotes: string | null
  internalNotes: string | null
}

const EXTERNAL_DEFAULTS: ExternalFormValues = {
  catalogProductId: null,
  quantity: null,
  repeatsAllowed: 0,
  instructions: null,
  validUntil: null,
  veterinaryCascade: false,
  createTask: false,
  clinicId: null,
  clinicalNotes: null,
  internalNotes: null,
}

/** Maps a validated item back to pre-validation form state for editing. */
function toFormValues(item: ExternalFormItem): ExternalFormValues {
  return {
    catalogProductId: item.catalogProductId,
    quantity: item.quantity,
    instructions: item.instructions,
    repeatsAllowed: item.repeatsAllowed,
    validUntil: item.validUntil,
    veterinaryCascade: item.veterinaryCascade,
    createTask: item.type === 'external_prescription_with_task',
    clinicId: item.type === 'external_prescription_with_task' ? item.clinicId : null,
    clinicalNotes: item.clinicalNotes,
    internalNotes: item.internalNotes,
  }
}

const baseExternalSchema = z.object({
  catalogProductId: z
    .string({ message: 'Please select a product' })
    .min(1, 'Please select a product'),
  quantity: z
    .number({ message: 'Please enter a quantity' })
    .positive('Quantity must be greater than 0')
    .multipleOf(0.01, 'Maximum 2 decimal places'),
  repeatsAllowed: z.number().int().min(0),
  instructions: z
    .string({ message: 'Instructions are required' })
    .min(1, 'Instructions are required'),
  validUntil: z.date({ message: 'Please specify when this prescription expires' }),
  veterinaryCascade: z.boolean(),
  createTask: z.boolean(),
  clinicalNotes: z.string().nullable(),
  internalNotes: z.string().nullable(),
})

/** When createTask is true, clinicId becomes required. */
const externalWithTaskSchema = baseExternalSchema.extend({
  createTask: z.literal(true),
  clinicId: z.string({ message: 'Please select a clinic' }).min(1, 'Please select a clinic'),
})

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ExternalTreatmentFormProps {
  defaultValues?: ExternalFormItem
  /** Default createTask when adding a new item (e.g. true for remote vets). */
  defaultCreateTask?: boolean
  /** Prescribing context - determines available options and form fields. */
  prescribingContext: PrescribingContext
  onSave: (item: ExternalFormItem) => void
  onCancel: () => void
  editing?: boolean
  /** Whether an async save is in progress — disables submit button. */
  saving?: boolean
}

export function ExternalTreatmentForm({
  defaultValues,
  defaultCreateTask = false,
  prescribingContext,
  onSave,
  onCancel,
  editing = false,
  saving = false,
}: ExternalTreatmentFormProps) {
  const isStandalone = prescribingContext === 'standalone'
  const [showingNewProduct, setShowingNewProduct] = useState(false)
  const { data: catalogProducts } = useListCatalogProducts()

  const defaults = defaultValues
    ? toFormValues(defaultValues)
    : { ...EXTERNAL_DEFAULTS, createTask: defaultCreateTask }

  const form = useAppForm({
    defaultValues: defaults,
    validators: {
      onSubmit: ({ value }) => {
        const schema = value.createTask ? externalWithTaskSchema : baseExternalSchema
        const res = schema.safeParse(value)
        if (!res.success) {
          const fieldErrors = zodErrorToFieldErrors(res.error)
          return { fields: fieldErrors.fields }
        }
      },
    },
    onSubmit: ({ value }) => {
      // Clinical/internal notes are only editable for standalone scripts (there is
      // currently no flow for a standalone treatment to later become part of a
      // consultation, so they can't appear on consult-related scripts). In-consult
      // edits round-trip any existing values from the defaults so they aren't wiped.
      const shared = {
        treatmentCategory: 'external' as const,
        catalogProductId: value.catalogProductId!,
        quantity: value.quantity!,
        instructions: value.instructions!,
        repeatsAllowed: isControlledSubstance ? 0 : (value.repeatsAllowed ?? 0),
        validUntil: value.validUntil!,
        veterinaryCascade: value.veterinaryCascade,
        clinicalNotes: value.clinicalNotes,
        internalNotes: value.internalNotes,
      }
      if (value.createTask) {
        onSave({ type: 'external_prescription_with_task', ...shared, clinicId: value.clinicId! })
      } else {
        onSave({ type: 'external_prescription', ...shared })
      }
    },
  })

  const catalogProductId = useStore(form.store, (s) => s.values.catalogProductId)
  const isControlledSubstance =
    catalogProducts?.find((product) => product.id === catalogProductId)?.isControlledSubstance ===
    true

  return (
    <form.AppForm>
      <div className="relative overflow-hidden pl-1 flex flex-col gap-3">
        {/* Product selection */}
        <form.AppField name="catalogProductId">
          {(field) => (
            <FieldWrapper
              field={field}
              label="Medication"
              required
              render={({ id, disabled }) => (
                <CatalogProductSearch
                  id={id}
                  value={field.state.value}
                  onChange={(id) => {
                    field.handleChange(id)
                    if (
                      catalogProducts?.find((product) => product.id === id)?.isControlledSubstance
                    ) {
                      form.setFieldValue('repeatsAllowed', 0)
                    }
                  }}
                  onAddNew={() => setShowingNewProduct(true)}
                  onBlur={field.handleBlur}
                  disabled={disabled || editing}
                  aria-invalid={field.state.meta.errors.length > 0}
                />
              )}
            />
          )}
        </form.AppField>

        {/* Inline catalog product creation */}
        <Dialog open={showingNewProduct} onOpenChange={setShowingNewProduct}>
          <DialogContent nested>
            <DialogHeader>
              <DialogTitle>Create New Medication</DialogTitle>
            </DialogHeader>
            <CatalogProductForm
              onSuccess={(id) => {
                form.setFieldValue('catalogProductId', id)
                form.setFieldValue('repeatsAllowed', 0)
                setShowingNewProduct(false)
              }}
              onCancel={() => setShowingNewProduct(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Quantity + refills */}
        <div className="grid grid-cols-2 gap-4">
          <form.AppField name="quantity">
            {(field) => <field.NumericInputField label="Quantity" placeholder="e.g. 14" required />}
          </form.AppField>

          <form.AppField name="repeatsAllowed">
            {(field) => (
              <field.NumericInputField
                label="Refills"
                placeholder="0"
                disabled={isControlledSubstance}
                description={
                  isControlledSubstance ? 'Controlled substances cannot have refills' : undefined
                }
              />
            )}
          </form.AppField>
        </div>

        {/* Instructions */}
        <form.AppField name="instructions">
          {(field) => (
            <field.TextareaField
              label="Instructions"
              placeholder="e.g. Give 1 tablet twice daily with food for 7 days"
              required
            />
          )}
        </form.AppField>

        {/* Valid until */}
        <form.AppField name="validUntil">
          {(field) => (
            <field.DatePickerField
              label="Valid Until"
              required
              presets={[{ days: 28 }, { months: 6 }, { years: 1 }]}
            />
          )}
        </form.AppField>

        {/* Veterinary cascade */}
        <form.AppField name="veterinaryCascade">
          {(field) => (
            <field.CheckboxField
              label="Veterinary cascade"
              description="Medication is prescribed in accordance with the veterinary cascade"
            />
          )}
        </form.AppField>

        {/* Create task */}
        <form.AppField name="createTask">
          {(field) => (
            <field.CheckboxField
              label="Create task"
              description="Creates a task for a clinic team to print and action this prescription"
              disabled={editing}
            />
          )}
        </form.AppField>

        {/* Clinic selector — only when creating a task */}
        <form.Subscribe selector={(s) => s.values.createTask}>
          {(createTask) =>
            createTask && (
              <form.AppField name="clinicId">
                {(field) => (
                  <FieldWrapper
                    field={field}
                    label="Clinic"
                    required
                    render={({ id, disabled }) => (
                      <ClinicSearchWithDefault
                        id={id}
                        value={field.state.value}
                        onChange={field.handleChange}
                        onBlur={field.handleBlur}
                        disabled={disabled}
                      />
                    )}
                  />
                )}
              </form.AppField>
            )
          }
        </form.Subscribe>

        {/* Clinical + internal notes — only for standalone scripts. Consult-related
            scripts capture clinical context in the consultation notes instead. */}
        {isStandalone && (
          <>
            <form.AppField name="clinicalNotes">
              {(field) => (
                <field.TextareaField
                  label="Clinical Notes"
                  description="Standalone treatments aren't part of a consultation. Use this field to optionally capture any clinical reasoning that would otherwise go in the consultation notes. Shown on the patient record, but not on the written prescription."
                  placeholder="e.g. Diagnosis, rationale, patient context"
                  rows={2}
                />
              )}
            </form.AppField>

            <form.AppField name="internalNotes">
              {(field) => (
                <field.TextareaField
                  label="Instructions for collection"
                  description="Administrative notes (collection details etc.) for Snoots staff only - not shown on the patient record or written prescription."
                  placeholder="e.g. Client wants to collect from Boots on High St"
                  rows={2}
                />
              )}
            </form.AppField>
          </>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="lg" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" size="lg" disabled={saving} onClick={() => form.handleSubmit()}>
            {saving ? <Loader2 className="size-3.5 mr-1.5 animate-spin" /> : null}
            Save
          </Button>
        </div>
      </div>
    </form.AppForm>
  )
}
